<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
          @submit="searchScheduleTemplates"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('scheduleTemplate.createTemplate') }}
        </v-btn>
      </div>
    </div>
    <v-row v-if="scheduleTemplatesComputed.length > 0">
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :items="scheduleTemplatesComputed"
          :options.sync="tableOptions"
          :server-items-length="scheduleTemplatesTotalCount"
          class="p-no-borders"
          disable-filtering
          hide-default-footer
          @page-count="pageCount = $event"
          @update:options="reloadScheduleTemplates"
        >
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('scheduleTemplate.table.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions">
              <p-btn-icon type="edit" @click="openEditDialog(item.id)"></p-btn-icon>
              <p-btn-icon type="delete" @click="deleteTemplate(item)"></p-btn-icon>
            </div>
          </template>
        </v-data-table>

        <v-pagination
          v-model="tablePage"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <v-row v-else-if="scheduleTemplatesLoaded && !search">
      <v-col>
        <v-divider class="mb-16"></v-divider>
        <p-no-data
          img="contract.png"
          :text="$t('contractTemplate.noTemplates')"
          :btn-text="$t('contractTemplate.createTemplate')"
          :hide-btn="!hasAccess()"
          @btn-click="openEditDialog(null)"
        ></p-no-data>
      </v-col>
    </v-row>

    <template-edit-dialog
      :dialog.sync="editDialog"
      :template="editedTemplate"
    ></template-edit-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions, mapState } from 'vuex'

import PSearchInput from '@/components/PSearchInput'
import PBtnIcon from '@/components/PBtnIcon'
import PNoData from '@/components/PNoData'

import TemplateEditDialog from './ScheduleTemplateEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'
import userSettingsMixin from '@/mixins/userSettings.mixin'

import eventBus from '@/eventBus'

export default {
  components: {
    PSearchInput,
    PBtnIcon,
    TemplateEditDialog,
    PNoData
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin,
    userSettingsMixin
  ],


  data () {
    return {
      search: '',
      editDialog: false,
      editedTemplate: null,
      tableOptions: {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: []
      },
      scheduleTemplatesLoaded: false,
      pageCount: 0,
      tablePage: 1
    }
  },

  watch: {
    currentSeasonId () {
      this.reloadScheduleTemplates(1)
    },

    tablePage (newTablePage) {
      this.tableOptions.page = newTablePage
      this.reloadScheduleTemplates(newTablePage)
    }
  },

  computed: {
    ...mapState('scheduleTemplate', ['scheduleTemplatesTotalCount']),
    ...mapGetters('scheduleTemplate', ['scheduleTemplates', 'scheduleTemplateById']),
    ...mapGetters('changes', ['isItemChanged']),

    scheduleTemplatesComputed () {
      return this.scheduleTemplates.map(scheduleTemplate => {
        return {
          ...scheduleTemplate,
          changed: this.isItemChanged(scheduleTemplate.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('scheduleTemplate.name'), value: 'name' },
        { text: '', value: 'changed' },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.scheduleTemplates.map(x => x.name)
      return items
    }
  },


  methods: {
    ...mapActions('scheduleTemplate', ['loadScheduleTemplates', 'loadScheduleTemplateById', 'deleteScheduleTemplate']),
    ...mapActions('placeholder', ['loadPlaceholdersScheduleGlobal']),

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchScheduleTemplates(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadScheduleTemplates()
      })
    },

    async openEditDialog (id) {
      try {
        let template = null

        if (id) {
          template = this.scheduleTemplateById(id)
          if (!template || !template.template) {
            this.startPageLoading()
            template = await this.loadScheduleTemplateById(id)
          }
        }

        this.editedTemplate = template
        this.editDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async deleteTemplate (template) {
      try {
        const confirm = await this.$confirm(this.$t('scheduleTemplate.deleteTemplateConfirm', template), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteScheduleTemplate(template.id)
          this.showSnackbarSuccess(this.$t('scheduleTemplate.templateDeleted'))
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async searchScheduleTemplates (val) {
      this.search = val
      this.tableOptions.page = 1
      await this.reloadScheduleTemplates()
    },

    async reloadScheduleTemplates () {
      this.saveTableOptions()
      try {
        this.startPageLoading()
        const sortBy = this.tableOptions.sortBy.length ? this.tableOptions.sortBy[0] : null
        const sortDesc = this.tableOptions.sortDesc.length ? this.tableOptions.sortDesc[0] : false

        await this.loadScheduleTemplates({
          skip: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
          take: this.tableOptions.itemsPerPage,
          search: this.search,
          sortBy,
          sortDesc
        })
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    saveTableOptions () {
      if (this.tableOptions.sortBy && this.tableOptions.sortBy.length) {
        this.userSettings.scheduleTemplatesTableOptionsSortBy = this.tableOptions.sortBy[0]
      }
      if (this.tableOptions.sortDesc && this.tableOptions.sortDesc.length) {
        this.userSettings.scheduleTemplatesTableOptionsSortDesc = this.tableOptions.sortDesc[0]
      }
      this.setUserSettings()
    },

    restoreTableOptions () {
      this.tableOptions.sortBy = []
      if (this.userSettings?.scheduleTemplatesTableOptionsSortBy) {
        this.tableOptions.sortBy.push(this.userSettings?.scheduleTemplatesTableOptionsSortBy)
      }

      this.tableOptions.sortDesc = []
      if (this.userSettings?.scheduleTemplatesTableOptionsSortDesc) {
        this.tableOptions.sortDesc.push(this.userSettings?.scheduleTemplatesTableOptionsSortDesc)
      }
    }
  },


  async created () {
    try {
      this.initEventBus()
      this.startPageLoading()
      await this.loadPlaceholdersScheduleGlobal()
      await this.loadScheduleTemplates({ take: this.tableOptions.itemsPerPage })
      this.restoreTableOptions()
      this.scheduleTemplatesLoaded = true
      if (this.$router.currentRoute.params?.goto) {
        await this.searchScheduleTemplates(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
